import { useState, useEffect, useRef } from "react"
import { useNavigate, Link } from "react-router-dom"
import { AlertCircle, Loader2, CheckCircle2, Shield, Users, Briefcase, ArrowRight } from "lucide-react"
import { getAuth, fetchSignInMethodsForEmail, createUserWithEmailAndPassword } from "firebase/auth"
import { doc, setDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../services/firebase"
import OrganizationService from "../services/OrganizationService"
import { useAuth } from "../contexts/AuthProvider"
import { RedirectUtils } from "../utils/redirectUtils"
import { useMixpanel } from "../hooks/useMixpanel"
import { sendEmail } from "../services/emailService"
import LOGO from "../assets/logo_h.svg"

const FF = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const T = {
  radius:       8,
  bg:           "#fff",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
}

const OTP_LENGTH  = 6
const API_BASE    = "https://hire100x-e1dd4.uc.r.appspot.com"
const INVITE_CODE = "FHXPAB"

const PERSONAL_DOMAINS = new Set([
  "gmail.com","yahoo.com","hotmail.com","outlook.com","live.com","msn.com",
  "icloud.com","me.com","mac.com","aol.com","protonmail.com","proton.me",
  "tutanota.com","zoho.com","yandex.com","mail.com","inbox.com","gmx.com",
  "fastmail.com","hey.com","rediffmail.com","qq.com","163.com","126.com",
])

const isBusinessEmail = (emailAddr) => {
  const domain = emailAddr.split("@")[1]?.toLowerCase()
  return domain && !PERSONAL_DOMAINS.has(domain)
}

const features = [
  { icon: Shield,    label: "Vetted Candidates"    },
  { icon: Users,     label: "Team Collaboration"   },
  { icon: Briefcase, label: "Smart Job Matching"   },
]

// ── useIsMobile ───────────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener("resize", handler)
    return () => window.removeEventListener("resize", handler)
  }, [])
  return isMobile
}

// ── Step indicator ────────────────────────────────────────────────────────────
const StepIndicator = ({ steps, current }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: 36 }}>
    {steps.map((label, i) => {
      const done   = i < current
      const active = i === current
      return (
        <div key={label} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: done || active ? "#111827" : T.bg,
              border: done || active ? "none" : "1px solid #d1d5db",
              color: done || active ? "#fff" : T.textTertiary,
              fontSize: 13, fontWeight: 700, fontFamily: FF,
              transition: "all 0.15s", flexShrink: 0,
            }}>
              {done ? <CheckCircle2 size={15} strokeWidth={2.5} /> : String(i + 1).padStart(2, "0")}
            </div>
            <span style={{
              fontSize: 11, fontWeight: 600,
              textTransform: "uppercase", letterSpacing: "0.06em",
              marginTop: 6, fontFamily: FF,
              color: done || active ? T.text : T.textTertiary,
            }}>
              {label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              height: 1, width: 52, margin: "0 8px", marginBottom: 20,
              background: i < current ? "#111827" : "#e5e7eb",
              transition: "background 0.2s", flexShrink: 0,
            }} />
          )}
        </div>
      )
    })}
  </div>
)

// ── OTP input ─────────────────────────────────────────────────────────────────
const OtpInput = ({ value, onChange }) => {
  const inputs   = useRef([])
  const isMobile = useIsMobile()
  const digits   = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] || "")

  const handleChange = (e, idx) => {
    const val  = e.target.value.replace(/\D/g, "").slice(-1)
    const next = [...digits]; next[idx] = val
    onChange(next.join(""))
    if (val && idx < OTP_LENGTH - 1) setTimeout(() => inputs.current[idx + 1]?.focus(), 0)
  }

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (digits[idx]) {
        const next = [...digits]; next[idx] = ""; onChange(next.join(""))
      } else if (idx > 0) {
        const next = [...digits]; next[idx - 1] = ""; onChange(next.join(""))
        setTimeout(() => inputs.current[idx - 1]?.focus(), 0)
      }
      e.preventDefault()
    } else if (e.key === "ArrowLeft"  && idx > 0)              inputs.current[idx - 1]?.focus()
      else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) inputs.current[idx + 1]?.focus()
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH)
    const next   = Array.from({ length: OTP_LENGTH }, (_, i) => pasted[i] || "")
    onChange(next.join(""))
    setTimeout(() => inputs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus(), 0)
  }

  return (
    <div style={{ display: "flex", gap: 10 }}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text" inputMode="numeric" autoComplete="off" maxLength={1}
          value={digit}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          onPaste={handlePaste}
          onFocus={(e) => { e.target.style.borderColor = "#111827"; e.target.style.borderWidth = "1.5px"; e.target.select() }}
          onBlur={(e)  => { e.target.style.borderColor = digit ? "#111827" : "#d1d5db"; e.target.style.borderWidth = digit ? "1.5px" : "1px" }}
          style={{
            width: isMobile ? 44 : 52, height: isMobile ? 50 : 58,
            textAlign: "center", fontSize: 22, fontWeight: 700, fontFamily: FF,
            color: "#111827",
            background: digit ? "#fff" : "#f9fafb",
            border: digit ? "1.5px solid #111827" : "1px solid #d1d5db",
            borderRadius: T.radius, outline: "none",
            transition: "border-color 0.12s, background 0.12s",
            caretColor: "#111827",
          }}
        />
      ))}
    </div>
  )
}

// ── Field ─────────────────────────────────────────────────────────────────────
const Field = ({ label, ...props }) => (
  <div>
    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.textMuted, fontFamily: FF, marginBottom: 7 }}>
      {label}
    </label>
    <input
      style={{
        width: "100%", height: 42, padding: "0 14px",
        border: "1px solid #d1d5db", borderRadius: T.radius,
        fontSize: 14, fontWeight: 500, color: T.text,
        background: T.bg, fontFamily: FF, outline: "none",
        transition: "border-color 0.12s", boxSizing: "border-box",
      }}
      onFocus={(e) => { e.target.style.borderColor = "#111827" }}
      onBlur={(e)  => { e.target.style.borderColor = "#d1d5db" }}
      {...props}
    />
  </div>
)

// ── PrimaryBtn ────────────────────────────────────────────────────────────────
const PrimaryBtn = ({ children, loading, ...props }) => (
  <button
    {...props}
    disabled={loading || props.disabled}
    style={{
      width: "100%", height: 42,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      border: "none", borderRadius: T.radius,
      background: loading || props.disabled ? "#9ca3af" : "#111827",
      color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: FF,
      cursor: loading || props.disabled ? "not-allowed" : "pointer",
      outline: "none", transition: "background 0.12s",
    }}
    onMouseEnter={(e) => { if (!loading && !props.disabled) e.currentTarget.style.background = "#27272a" }}
    onMouseLeave={(e) => { if (!loading && !props.disabled) e.currentTarget.style.background = "#111827" }}
  >
    {loading ? <Loader2 size={16} style={{ animation: "ob-spin 0.8s linear infinite" }} /> : children}
  </button>
)

// ── GhostBtn ──────────────────────────────────────────────────────────────────
const GhostBtn = ({ children, onClick, disabled, style }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      height: 42, padding: "0 16px",
      display: "inline-flex", alignItems: "center", gap: 8,
      border: "1px solid #d1d5db", borderRadius: T.radius,
      background: T.bg, color: T.textMuted,
      fontSize: 14, fontWeight: 500, fontFamily: FF,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      outline: "none", transition: "border-color 0.12s",
      ...style,
    }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#9ca3af" }}
    onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#d1d5db" }}
  >
    {children}
  </button>
)

// ── OnboardOrgPage ────────────────────────────────────────────────────────────
const OnboardOrgPage = () => {
  const [step, setStep]                       = useState(0)
  const [email, setEmail]                     = useState("")
  const [userName, setUserName]               = useState("")
  const [otp, setOtp]                         = useState("")
  const [organizationName, setOrganizationName] = useState("")
  const [inviteCode, setInviteCode]           = useState("")
  const [password, setPassword]               = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError]                     = useState("")
  const [loading, setLoading]                 = useState(false)
  const [retrySeconds, setRetrySeconds]       = useState(0)

  const navigate        = useNavigate()
  const { refreshAuth } = useAuth()
  const { track }       = useMixpanel()
  const isMobile        = useIsMobile()

  useEffect(() => {
    if (retrySeconds <= 0) return
    const t = setTimeout(() => setRetrySeconds((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [retrySeconds])

  // ── API helpers ───────────────────────────────────────────────────────────
  const sendOtp = async (emailAddr) => {
    const res  = await fetch(`${API_BASE}/verification/send`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailAddr }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || "Failed to send access code")
    return data
  }

  const verifyOtp = async (emailAddr, code) => {
    const res  = await fetch(`${API_BASE}/verification/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailAddr, otp: code }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || "Verification failed")
    return data
  }

  // ── Step 0: identity ──────────────────────────────────────────────────────
  const handleIdentitySubmit = async (e) => {
    e.preventDefault(); setError("")
    if (!email || !email.includes("@")) { setError("Please enter a valid email address"); return }
    if (!isBusinessEmail(email))        { setError("Please use a business email address. Personal email providers are not accepted."); return }
    setLoading(true)
    try {
      const auth    = getAuth()
      const methods = await fetchSignInMethodsForEmail(auth, email)
      if (methods.length > 0) {
        setError("An account with this email already exists. Please log in instead.")
        return
      }
      await sendOtp(email)
      setRetrySeconds(45)
      setStep(1)
    } catch (err) { setError(err.message) }
    finally { setLoading(false) }
  }

  // ── Step 1: verify OTP ────────────────────────────────────────────────────
  const handleOtpSubmit = async (e) => {
    e.preventDefault(); setError("")
    if (otp.replace(/\s/g, "").length < OTP_LENGTH) { setError("Please enter the full 6-digit code"); return }
    setLoading(true)
    try {
      await verifyOtp(email, otp)
      setStep(2)
    } catch (err) { setError(err.message) }
    finally { setLoading(false) }
  }

  const handleResend = async () => {
    if (retrySeconds > 0) return
    setError(""); setOtp("")
    try { await sendOtp(email); setRetrySeconds(45) }
    catch (err) { setError(err.message) }
  }

  // ── Step 2: create account ────────────────────────────────────────────────
  const handleComplete = async (e) => {
    e.preventDefault(); setError("")
    if (!userName.trim())                                     { setError("Please enter your full name"); return }
    if (!organizationName.trim())                             { setError("Please enter your organization name"); return }
    if (inviteCode.trim().toUpperCase() !== INVITE_CODE)      { setError("Invalid invite code. Contact hello@resolut.co.in to get access."); return }
    if (password.length < 8)                                  { setError("Password must be at least 8 characters"); return }
    if (password !== confirmPassword)                         { setError("Passwords don't match"); return }
    setLoading(true)
    try {
      const auth   = getAuth()
      const result = await createUserWithEmailAndPassword(auth, email, password)
      const user   = result.user

      await setDoc(doc(db, "users", user.uid), {
        uid:         user.uid,
        email:       user.email,
        name:        userName,
        createdAt:   serverTimestamp(),
        lastLogin:   serverTimestamp(),
        accountType: "employer",
      })

      try {
        await OrganizationService.createOrganization(organizationName, user.uid, email, userName)
      } catch (orgError) {
        console.error("Error creating organization:", orgError)
      }

      track("Account Created", { name: organizationName, type: "employer" })
      sendEmail({
        email,
        subject: "Welcome to Resolut",
        text: `Hi ${organizationName} Team,\n\nWelcome to the Resolut Club — we're excited to have you on board!\n\nComplete your company profile at https://resolut.co.in/profile\n\nBest,\nPatrick\nCo-founder, Resolut`,
      })

      if (refreshAuth) await refreshAuth()
      await new Promise((r) => setTimeout(r, 500))
      RedirectUtils.redirectToStoredUrl(navigate, "/profile")
    } catch (err) {
      const msgs = {
        "auth/email-already-in-use": "An account already exists with this email. Please log in.",
        "auth/invalid-email":        "Invalid email address format",
        "auth/weak-password":        "Password is too weak. Please use at least 8 characters",
      }
      setError(msgs[err.code] || err.message)
    } finally { setLoading(false) }
  }

  const steps = ["Identity", "Verify", "Access"]

  const passwordsMatch  = confirmPassword === password
  const passwordLongEnough = password.length >= 8

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: T.bg, fontFamily: FF }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        @keyframes ob-spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        input:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>

      {/* ── LEFT PANEL — hidden on mobile ── */}
      {!isMobile && (
        <div style={{
          width: "35%", flexShrink: 0, minHeight: "100vh",
          background: "rgb(249,249,247)",
          display: "flex", flexDirection: "column", justifyContent: "space-between",
          padding: "44px 48px",
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 64 }}>
              <div style={{ width: 30, height: 30, borderRadius: T.radius, background: "#111827", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img className="w-4 h-4 brightness-0 invert" src={LOGO} alt="Logo" />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FF, letterSpacing: "0.04em" }}>Resolut</span>
            </div>

            <p style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textTertiary, fontFamily: FF, margin: "0 0 14px" }}>
              Sector 01 · Employers
            </p>

            <h2 style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.15, color: T.text, margin: "0 0 16px", fontFamily: FF, letterSpacing: "-0.025em" }}>
              Top 1% of Talent<br />Hired Fast
            </h2>

            <p style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.7, color: T.textTertiary, margin: "0 0 44px", fontFamily: FF }}>
              Build your dream team faster.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {features.map(({ icon: Icon, label }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 32, height: 32, borderRadius: T.radius, background: "#fff", border: "0.5px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon size={14} color={T.textTertiary} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.textMuted, fontFamily: FF }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 20 }}>
            <a href="/terms" style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textDecoration: "none" }}
              onMouseEnter={(e) => e.target.style.color = T.text} onMouseLeave={(e) => e.target.style.color = T.textTertiary}>Terms of Use</a>
            <a href="/privacy" style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textDecoration: "none" }}
              onMouseEnter={(e) => e.target.style.color = T.text} onMouseLeave={(e) => e.target.style.color = T.textTertiary}>Privacy Policy</a>
          </div>
        </div>
      )}

      {/* ── RIGHT PANEL ── */}
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "center",
        padding: isMobile ? "32px 24px 48px" : "48px 72px",
        background: T.bg,
      }}>
        <div style={{ width: "100%", maxWidth: 480 }}>

          {/* Mobile logo */}
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 36 }}>
              <div style={{ width: 30, height: 30, borderRadius: T.radius, background: "#111827", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img className="w-4 h-4 brightness-0 invert" src={LOGO} alt="Logo" />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FF, letterSpacing: "0.04em" }}>Resolut</span>
            </div>
          )}

          <div style={{ marginBottom: 36 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: T.text, margin: "0 0 8px", fontFamily: FF, letterSpacing: "-0.02em" }}>
              Set up your employer account
            </h1>
            <p style={{ fontSize: 14, fontWeight: 500, color: T.textTertiary, margin: 0, fontFamily: FF }}>
              Find top talent for your team.
            </p>
          </div>

          <StepIndicator steps={steps} current={step} />

          {/* Error */}
          {error && (
            <div style={{ marginBottom: 24, padding: "11px 14px", background: "#fef2f2", border: "0.5px solid #fecaca", borderRadius: T.radius, display: "flex", alignItems: "center", gap: 10 }}>
              <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 500, color: "#b91c1c", fontFamily: FF }}>{error}</span>
            </div>
          )}

          {/* ── STEP 0: IDENTITY ── */}
          {step === 0 && (
            <form onSubmit={handleIdentitySubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <Field
                label="Work email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" required
              />

              <PrimaryBtn loading={loading} type="submit">
                Continue <ArrowRight size={15} />
              </PrimaryBtn>

              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ flex: 1, height: "0.5px", background: "#e5e7eb" }} />
                <span style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF }}>or</span>
                <div style={{ flex: 1, height: "0.5px", background: "#e5e7eb" }} />
              </div>

              <GhostBtn onClick={() => {}} style={{ width: "100%", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
                </svg>
                Continue with SSO
              </GhostBtn>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textAlign: "center" }}>
                  Already have an account?{" "}
                  <Link to="/login" style={{ color: T.text, fontWeight: 600, textDecoration: "underline" }}>Log in</Link>
                </p>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textAlign: "center" }}>
                  Looking for a job?{" "}
                  <Link to="/register" style={{ color: T.text, fontWeight: 600, textDecoration: "underline" }}>Candidate sign up</Link>
                </p>
              </div>
            </form>
          )}

          {/* ── STEP 1: VERIFY ── */}
          {step === 1 && (
            <form onSubmit={handleOtpSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ padding: "11px 14px", background: "#ecfdf5", border: "0.5px solid #a7f3d0", borderRadius: T.radius, display: "flex", alignItems: "center", gap: 10 }}>
                <CheckCircle2 size={15} color="#065f46" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#065f46", fontFamily: FF }}>Verification signal transmitted.</span>
              </div>

              <div>
                <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 600, color: T.textMuted, fontFamily: FF }}>Enter access code sent to:</p>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: T.text, fontFamily: FF }}>{email}</p>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.textMuted, fontFamily: FF, marginBottom: 10 }}>
                  6-digit access code
                </label>
                <OtpInput value={otp} onChange={setOtp} />
              </div>

              <PrimaryBtn type="submit" loading={loading}>
                <Shield size={15} /> Verify access
              </PrimaryBtn>

              <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                <button type="button" onClick={handleResend}
                  style={{
                    background: "none", border: "none", padding: 0,
                    fontSize: 13, fontWeight: 600, fontFamily: FF,
                    color: retrySeconds > 0 ? T.textTertiary : T.text,
                    cursor: retrySeconds > 0 ? "default" : "pointer",
                    opacity: retrySeconds > 0 ? 0.55 : 1,
                  }}>
                  {retrySeconds > 0 ? `Retry in ${retrySeconds}s` : "Retry"}
                </button>
                <button type="button"
                  onClick={() => { setStep(0); setOtp(""); setError("") }}
                  style={{ background: "none", border: "none", padding: 0, fontSize: 13, fontWeight: 600, fontFamily: FF, color: T.textTertiary, cursor: "pointer" }}>
                  Modify email
                </button>
              </div>

              <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textAlign: "center" }}>
                Already have an account?{" "}
                <Link to="/login" style={{ color: T.text, fontWeight: 600, textDecoration: "underline" }}>Log in</Link>
              </p>
            </form>
          )}

          {/* ── STEP 2: ACCESS ── */}
          {step === 2 && (
            <form onSubmit={handleComplete} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div style={{ padding: "11px 14px", background: "#ecfdf5", border: "0.5px solid #a7f3d0", borderRadius: T.radius, display: "flex", alignItems: "center", gap: 10 }}>
                <CheckCircle2 size={15} color="#065f46" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: "#065f46", fontFamily: FF }}>Identity verified. Initialize profile.</span>
              </div>

              <div style={{ padding: "10px 14px", background: "#f9fafb", border: "0.5px solid #e5e7eb", borderRadius: T.radius }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: T.textMuted, fontFamily: FF }}>{email} — securely verified</span>
              </div>

              {/* Full name */}
              <Field
                label="Your full name" type="text" value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Jane Smith" required
              />

              {/* Organization name */}
              <Field
                label="Organization name" type="text" value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Your company or team name" required
              />

              {/* Invite code */}
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.textMuted, fontFamily: FF, marginBottom: 7 }}>
                  Invite code
                </label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="XXXXXX"
                  maxLength={8}
                  required
                  style={{
                    width: "100%", height: 42, padding: "0 14px",
                    border: "1px solid #d1d5db", borderRadius: T.radius,
                    fontSize: 14, fontWeight: 600, color: T.text,
                    letterSpacing: "0.15em", background: T.bg,
                    fontFamily: FF, outline: "none",
                    transition: "border-color 0.12s", boxSizing: "border-box",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "#111827" }}
                  onBlur={(e)  => { e.target.style.borderColor = "#d1d5db" }}
                />
                <p style={{ margin: "5px 0 0", fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF }}>
                  Don't have one?{" "}
                  <a href="mailto:hello@resolut.co.in" style={{ color: T.text, fontWeight: 600 }}>Contact us</a>
                </p>
              </div>

              {/* Password */}
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.textMuted, fontFamily: FF, marginBottom: 7 }}>
                  Password
                </label>
                <input
                  type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters" required
                  style={{
                    width: "100%", height: 42, padding: "0 14px",
                    border: `1px solid ${password && !passwordLongEnough ? "#dc2626" : "#d1d5db"}`,
                    borderRadius: T.radius, fontSize: 14, fontWeight: 500, color: T.text,
                    background: T.bg, fontFamily: FF, outline: "none",
                    transition: "border-color 0.12s", boxSizing: "border-box",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = password && !passwordLongEnough ? "#dc2626" : "#111827" }}
                  onBlur={(e)  => { e.target.style.borderColor = password && !passwordLongEnough ? "#dc2626" : "#d1d5db" }}
                />
                {password && !passwordLongEnough && (
                  <p style={{ margin: "6px 0 0", fontSize: 12, fontWeight: 500, color: "#dc2626", fontFamily: FF }}>
                    Password must be at least 8 characters
                  </p>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.textMuted, fontFamily: FF, marginBottom: 7 }}>
                  Confirm password
                </label>
                <input
                  type="password" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password" required
                  style={{
                    width: "100%", height: 42, padding: "0 14px",
                    border: `1px solid ${confirmPassword && !passwordsMatch ? "#dc2626" : "#d1d5db"}`,
                    borderRadius: T.radius, fontSize: 14, fontWeight: 500, color: T.text,
                    background: T.bg, fontFamily: FF, outline: "none",
                    transition: "border-color 0.12s", boxSizing: "border-box",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = confirmPassword && !passwordsMatch ? "#dc2626" : "#111827" }}
                  onBlur={(e)  => { e.target.style.borderColor = confirmPassword && !passwordsMatch ? "#dc2626" : "#d1d5db" }}
                />
                {confirmPassword && !passwordsMatch && (
                  <p style={{ margin: "6px 0 0", fontSize: 12, fontWeight: 500, color: "#dc2626", fontFamily: FF }}>
                    Passwords don't match
                  </p>
                )}
              </div>

              <PrimaryBtn
                loading={loading} type="submit"
                disabled={!passwordLongEnough || !confirmPassword || !passwordsMatch}
              >
                Create employer account <ArrowRight size={15} />
              </PrimaryBtn>

              <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textAlign: "center" }}>
                Already have an account?{" "}
                <Link to="/login" style={{ color: T.text, fontWeight: 600, textDecoration: "underline" }}>Log in</Link>
              </p>
            </form>
          )}

          {/* Mobile footer */}
          {isMobile && (
            <div style={{ display: "flex", gap: 20, marginTop: 48 }}>
              <a href="/terms" style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textDecoration: "none" }}>Terms of Use</a>
              <a href="/privacy" style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textDecoration: "none" }}>Privacy Policy</a>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default OnboardOrgPage